import { expectedPendingFor } from "./nodeCreationShapes.js";

/**
 * ADR-0153 Phase 5 — the console findings channel's pure core.
 *
 * ONE path-driven resolver from validator findings to nodes, consumed by the
 * inspector shell's banner, the canvas badge sweep, and the operation-level
 * roll-up — zero per-inspector wiring, so a new node type's findings render by
 * existing. House rule honored throughout: a step is identified by its label,
 * never by a UUID or a raw path.
 *
 * Severity model (owner-picked option C): a node's worst finding decides its
 * treatment — `error` (red) > `warning` (amber) > `pending` (neutral,
 * "finish this in Atlas"). `pending` is an ERROR the node type DECLARED as its
 * unfinished-by-design state (file_provider before its upload) — the pipeline
 * still cannot run, but nobody did anything wrong, and rendering it red would
 * teach people to ignore red.
 */

export type ValidationFinding = {
  path: string;
  code: string;
  message: string;
  level: "error" | "warning";
};

export type NodeLike = { id: string; nodeType: string; label: string };

export type ResolvedFinding = {
  level: "error" | "warning";
  /** "pending" when the node type declared this finding unfinished-by-design. */
  treatment: "error" | "warning" | "pending";
  stepLabel: string | null;
  nodeId: string | null;
  /** Path remainder below the node, or the full path for non-node findings. */
  field: string | null;
  code: string;
  message: string;
  /** The declared finish-this-in-Atlas note, for pending findings. */
  pendingNote: string | null;
};

export type NodeFindingsSummary = {
  /** The node's worst treatment, deciding badge and banner styling. */
  severity: "error" | "warning" | "pending";
  findings: ResolvedFinding[];
};

const NODE_PATH_BY_ID = /^spec\.graph\.nodes\.([^.[\]]+)\.?(.*)$/;
const NODE_PATH_BY_INDEX = /^spec\.graph\.nodes\[(\d+)\]\.?(.*)$/;

/** Resolve a finding's node from either path grammar the validator emits. */
export function resolveFindingNode(
  path: string,
  nodes: NodeLike[]
): { node: NodeLike; field: string | null } | null {
  const byId = NODE_PATH_BY_ID.exec(path);
  if (byId) {
    const node = nodes.find((n) => n.id === byId[1]);
    if (node) return { node, field: byId[2] || null };
  }
  const byIndex = NODE_PATH_BY_INDEX.exec(path);
  if (byIndex) {
    const node = nodes[Number(byIndex[1])];
    if (node) return { node, field: byIndex[2] || null };
  }
  return null;
}

export function resolveFinding(finding: ValidationFinding, nodes: NodeLike[]): ResolvedFinding {
  const hit = resolveFindingNode(finding.path, nodes);
  const pending = hit ? expectedPendingFor(hit.node.nodeType, { code: finding.code, field: hit.field }) : null;
  return {
    level: finding.level,
    treatment: pending ? "pending" : finding.level,
    stepLabel: hit ? hit.node.label : null,
    nodeId: hit ? hit.node.id : null,
    field: hit ? hit.field : finding.path,
    code: finding.code,
    message: finding.message,
    pendingNote: pending ? pending.note : null,
  };
}

/**
 * Group findings by node. Non-node findings land under the `operation` key so
 * the roll-up shows them and nothing is ever dropped (an unresolvable path is
 * still a finding — never-drop applies to the resolver too).
 */
export function groupFindings(
  findings: ValidationFinding[],
  nodes: NodeLike[]
): { byNode: Map<string, NodeFindingsSummary>; operation: ResolvedFinding[]; all: ResolvedFinding[] } {
  const all = findings.map((f) => resolveFinding(f, nodes));
  const byNode = new Map<string, NodeFindingsSummary>();
  const operation: ResolvedFinding[] = [];
  for (const f of all) {
    if (!f.nodeId) {
      operation.push(f);
      continue;
    }
    const entry = byNode.get(f.nodeId) ?? { severity: "pending" as const, findings: [] };
    entry.findings.push(f);
    byNode.set(f.nodeId, entry);
  }
  for (const entry of byNode.values()) {
    entry.severity = worstTreatment(entry.findings);
  }
  return { byNode, operation, all };
}

function worstTreatment(findings: ResolvedFinding[]): "error" | "warning" | "pending" {
  if (findings.some((f) => f.treatment === "error")) return "error";
  if (findings.some((f) => f.treatment === "warning")) return "warning";
  return "pending";
}

/** Badge tooltip for a node — counts in business language, no codes, no paths. */
export function findingsBadgeTitle(summary: NodeFindingsSummary): string {
  const errors = summary.findings.filter((f) => f.treatment === "error").length;
  const warnings = summary.findings.filter((f) => f.treatment === "warning").length;
  const pending = summary.findings.filter((f) => f.treatment === "pending").length;
  const parts: string[] = [];
  if (errors > 0) parts.push(`${errors} ${errors === 1 ? "problem" : "problems"}`);
  if (warnings > 0) parts.push(`${warnings} ${warnings === 1 ? "warning" : "warnings"}`);
  if (pending > 0) parts.push("unfinished — complete this step in Atlas");
  return parts.join(" · ");
}

/** A humanized field name for the roll-up table — the last path segment,
 *  underscores to spaces; never a UUID, never a dotted machine path. */
export function humanizeField(field: string | null): string {
  if (!field) return "";
  const last = field.split(".").pop() ?? field;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(last)) return "";
  return last.replace(/_/g, " ");
}
